import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';

import {
  AvailableLocale,
  DEFAULT_LOCALE,
  getLocale,
  loadLocaleMessages,
  setLocale as persistLocale,
} from 'config/locale';

interface LocaleContextValue {
  locale: AvailableLocale;
  setLocale: (locale: AvailableLocale) => void;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within I18nProvider');
  }
  return context;
}

interface I18nProviderProps {
  children: ReactNode;
  selectedLocale?: AvailableLocale;
}

export default function I18nProvider({ children, selectedLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<AvailableLocale>(() => selectedLocale ?? getLocale());
  const [messages, setMessages] = useState<Record<string, string> | null>(null);

  const setLocale = useCallback((nextLocale: AvailableLocale) => {
    persistLocale(nextLocale);
    setLocaleState(nextLocale);
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadLocaleMessages(locale).then((loadedMessages) => {
      if (!cancelled) {
        setMessages(loadedMessages);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  const contextValue = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  if (!messages) return null;

  return (
    <LocaleContext.Provider value={contextValue}>
      <IntlProvider locale={locale} messages={messages} defaultLocale={DEFAULT_LOCALE}>
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}
